import { useState, useMemo, useEffect } from "react";
import SummaryCards from "./summaryCards";
import Filters from "./filters";
import KanbanBoard from "./kanbanBoard";
import BudgetHistoryModal from "./budgetHistoryModal";
import { Budget, BudgetViewType, BudgetSummary } from "./types";
import { mockSummary } from "./mockData";
import { fetchBudgetsKanban, fetchBudgetsKanbanSummary, fetchBudgetUserProfile } from "../../api/fetchBudget";
import { IBudgetKanbanItem } from "../../models/budget";
import { BudgetStatusFilterEnum } from "../../enums/budgetStatus";
import { getRouteByProfile } from "./budgetCard/utils";
import { useNavigate } from "react-router-dom";

interface BudgetsKanbanProps {
  persona?: string;
}

const mapApiBudgetToBudget = (apiBudget: IBudgetKanbanItem): Budget => {
  const formatDate = (dateString?: string): string => {
    if (!dateString) return "";
    try {
      let date: Date;
      const hasTimezone = dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/) || dateString.match(/[+-]\d{4}$/);
      
      if (hasTimezone) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString + 'Z');
      }
      
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} - ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const mapStatus = (status: string): Budget["status"] => {
    const statusMap: Record<string, Budget["status"]> = {
      "Pendente": "pendente",
      "Aprovado": "aceito",
      "Enviado": "enviado",
      "Alterado": "alterado",
      "Contraproposta": "contraproposta",
      "Recusado": "recusado",
      "Concluído": "concluido",
      SENT: "enviado",
      PENDING: "alterado",
      COUNTER_PROPOSED: "contraproposta",
      APPROVED: "aceito",
      APPROVED_BY_FACTORY: "aceito",
      REJECTED: "recusado",
    };
    return statusMap[status] || "pendente";
  };

  const mapColumn = (status: string, kanbanStep?: string): Budget["column"] => {
    if (kanbanStep) {
      if (kanbanStep === "IN_REVIEW") {
        return "pendente";
      }
      if (kanbanStep === "PENDING") {
        return "enviado";
      }
      if (kanbanStep === "ACCEPTED") {
        return "concluido";
      }
    }
    
    const statusLower = status.toLowerCase();
    if (statusLower === "aprovado" || statusLower === "approved" || statusLower === "concluído" || statusLower === "completed") {
      return "concluido";
    }
    if (statusLower === "enviado" || statusLower === "sent") {
      return "enviado";
    }
    
    return "pendente";
  };

  const mapRecipient = (recipient: string): "Consultor da concessionária" | "Volks|total" | "Gestor de Frotas" | "Não identificado" => {
    if (recipient === "FACTORY") {
      return "Volks|total";
    }
    if (recipient === "CONSULTANT") {
      return "Consultor da concessionária";
    }
    if (recipient === "MANAGER") {
      return "Gestor de Frotas";
    }
    return "Não identificado";
  };

  return {
    id: apiBudget.uuid,
    budgetNumber: apiBudget.budgetNumber,
    recipient: mapRecipient(apiBudget.recipient),
    date: formatDate(apiBudget.updatedAt),
    version: `v ${apiBudget.lastVersionNumber}.0`,
    status: mapStatus(apiBudget.status),
    column: mapColumn(apiBudget.status, apiBudget.kanbanStep),
    consultant: apiBudget.consultant || "Não identificado",
  };
};

const BudgetsKanban = ({ persona }: BudgetsKanbanProps) => {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [summary, setSummary] = useState<BudgetSummary>(mockSummary);
  const [viewType, setViewType] = useState<BudgetViewType>("geral");
  const [chassisFilter, setChassisFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<BudgetStatusFilterEnum | undefined>(undefined);
  const [startDateFilter, setStartDateFilter] = useState<string | undefined>(undefined);
  const [endDateFilter, setEndDateFilter] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCreateBudget = async () => {
    const userProfile = await fetchBudgetUserProfile();
    navigate(getRouteByProfile(userProfile))
  };

  const handleChassisSearch = (chassis: string) => {
    setChassisFilter(chassis);
  };

  const handleStatusChange = (status?: BudgetStatusFilterEnum) => {
    setStatusFilter(status);
  };

  const handleDateRangeChange = (startDate?: string, endDate?: string) => {
    setStartDateFilter(startDate);
    setEndDateFilter(endDate);
  };

  useEffect(() => {
    const loadBudgets = async () => {
      setLoading(true);
      try {
        const baseFilters: any = {
          page: 1,
          limit: 50,
        };

        if (chassisFilter) {
          baseFilters.chassis = chassisFilter;
        }

        if (startDateFilter) {
          baseFilters.startUpdatedAt = `${startDateFilter}T00:00:00`;
        }

        if (endDateFilter) {
          baseFilters.endUpdatedAt = `${endDateFilter}T23:59:59`;
        }

        const [pendenteResponse, enviadoResponse, concluidoResponse, summaryResponse] = await Promise.all([
          fetchBudgetsKanban({ ...baseFilters, kanbanStep: "IN_REVIEW", status: statusFilter }),
          fetchBudgetsKanban({ ...baseFilters, kanbanStep: "PENDING", status: statusFilter }),
          fetchBudgetsKanban({ ...baseFilters, kanbanStep: "ACCEPTED", status: statusFilter }),
          fetchBudgetsKanbanSummary({ ...baseFilters, status: statusFilter }),
        ]);

        const allBudgets = [
          ...pendenteResponse.budgets.map((budget) => mapApiBudgetToBudget({ ...budget, kanbanStep: "IN_REVIEW" })),
          ...enviadoResponse.budgets.map((budget) => mapApiBudgetToBudget({ ...budget, kanbanStep: "PENDING" })),
          ...concluidoResponse.budgets.map((budget) => mapApiBudgetToBudget({ ...budget, kanbanStep: "ACCEPTED" })),
        ];

        let filteredBudgets = allBudgets;

        setBudgets(filteredBudgets);
        setSummary(summaryResponse);
      } catch (error) {
        console.error("Erro ao carregar budgets:", error);
        setBudgets([]);
        setSummary(mockSummary);
      } finally {
        setLoading(false);
      }
    };

    loadBudgets();
  }, [chassisFilter, statusFilter, startDateFilter, endDateFilter]);

  const handleViewHistory = (budgetId: string) => {
    setSelectedBudgetId(budgetId);
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedBudgetId(null);
  };

  const handleAction = (budgetId: string, action: string) => {
    console.log("Ação no orçamento:", budgetId, action);
  };

  return (
    <div className="p-5">
      <div className="mb-5">
        <SummaryCards summary={summary} loading={loading} />
      </div>

      <div className="mb-5">
        <h2 className="text-2xl font-bold mb-4">Orçamentos</h2>
        <Filters
          onCreateBudget={handleCreateBudget}
          onChassisSearch={handleChassisSearch}
          onStatusChange={handleStatusChange}
          onDateRangeChange={handleDateRangeChange}
          persona={persona}
        />
      </div>

      <div className="mt-5">
        <KanbanBoard
          budgets={budgets}
          viewType={viewType}
          onViewTypeChange={setViewType}
          onViewHistory={handleViewHistory}
          onAction={handleAction}
        />
      </div>

      {selectedBudgetId && (
        <BudgetHistoryModal
          show={showHistoryModal}
          budgetId={selectedBudgetId}
          consultant={budgets.find((budget) => budget.id === selectedBudgetId)?.consultant || "Não identificado"}
          onClose={handleCloseHistoryModal}
        />
      )}
    </div>
  );
};

export default BudgetsKanban;
